import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { FaExternalLinkAlt, FaGlobe, FaBuilding, FaUsers, FaChartLine } from "react-icons/fa";
import "./Group Companies.css";
import Footer from "../../components/Footer";
import assetService from "../../services/assetService";
import cacheService from "../../services/cacheService";

// Import company logos (local fallbacks)
import aiplanetLogo from "../../assets/images/GroupCompanies/aiplanetlogo.svg";
import infodriveLogo from "../../assets/images/GroupCompanies/infodrive-logo.svg";
import exalioTechLogo from "../../assets/images/GroupCompanies/exalio-tech-logo.svg";
import vardaanUkLogo from "../../assets/images/GroupCompanies/vardaan-uk-logo.svg";
import vardaanCyberLogo from "../../assets/images/GroupCompanies/vardaan-cyber-logo.svg";
import vardaanAndsLogo from "../../assets/images/GroupCompanies/vardaan-ands-logo.svg";
import placeholderLogo from "../../assets/images/GroupCompanies/placeholder-logo.svg";

// IndexedDB cache key
const LOGOS_CACHE_KEY = 'group_companies_logos';

const GroupCompanies = () => {
  const history = useHistory();
  const [loadedImages, setLoadedImages] = useState({});
  const [cachedLogos, setCachedLogos] = useState({});

  // Logo mapping: company ID -> { s3Name, localFallback }
  const logoMapping = {
    1: { s3Name: 'aiplanet-logo.svg', localFallback: aiplanetLogo },
    2: { s3Name: 'infodrive-logo.svg', localFallback: infodriveLogo },
    3: { s3Name: 'exalio-tech-logo.svg', localFallback: exalioTechLogo },
    4: { s3Name: 'vardaan-uk-logo.svg', localFallback: vardaanUkLogo },
    5: { s3Name: 'vardaan-cyber-logo.svg', localFallback: vardaanCyberLogo },
    6: { s3Name: 'vardaan-ands-logo.svg', localFallback: vardaanAndsLogo }
  };

  // Mock data for group companies
  const groupCompanies = [
    {
      id: 1,
      name: "AI Planet",
      website: "https://aiplanet.com/",
      description: "Accelerating AI innovation with a platform trusted by 300k+ global community and leading organizations. Delivering 20x faster time to market, up to 30x infrastructure cost savings, and 10x productivity gains for enterprises worldwide. Enabling businesses to build, deploy, and scale AI solutions with cutting-edge technology and comprehensive support.",
      logo: aiplanetLogo,
      industry: "AI Platform & Services",
      founded: "2018",
      employees: "200+",
      services: ["AI Platform", "ML Model Development", "AI Infrastructure", "Enterprise AI Solutions"],
      color: "#e3edfa"
    },
    {
      id: 2,
      name: "InfoDrive Analytics",
      website: "https://infodriveanalytics.com/",
      description: "An international organization serving customers across industries around the globe, offering a variety of IT services including ERP, that help them differentiate from their competition. Our practices span from Business Solutions to Technology Solutions, with our Strategy practice bringing pragmatic solutions that solve problems while enhancing competitive advantage through the right technology strategy.",
      logo: infodriveLogo,
      industry: "Data Analytics",
      founded: "2015",
      employees: "150+",
      services: ["Business Intelligence", "Predictive Analytics", "Data Visualization", "Machine Learning"],
      color: "#e3edfa"
    },
    {
      id: 3,
      name: "ExalioTech",
      website: "https://exaliotech.com/",
      description: "Pioneering the future of intelligent business by helping governments and businesses embrace intelligent systems that drive efficiency, transparency, and innovation at scale. As a next-gen technology partner, Exalio helps organizations unlock their potential through AI-driven transformation, data mastery, and scalable digital solutions that ensure long-term success and scalability.",
      logo: exalioTechLogo,
      industry: "Cloud Technology",
      founded: "2018",
      employees: "200+",
      services: ["Cloud Solutions", "Enterprise Software", "Digital Innovation", "Tech Consulting"],
      color: "#e3edfa"
    },
    {
      id: 4,
      name: "Vardaan Global UK",
      website: "https://vardaanglobal.uk/",
      description: "A leading cybersecurity and ERP solutions provider engaged in consulting on projects across global regions, operating from offices in the UK, Malaysia, and India. We offer a wide range of IT consulting services including implementation, support, and managed services to clients in banking, insurance, education, and other emerging sectors with experienced functional and techno-functional experts.",
      logo: vardaanUkLogo,
      industry: "Financial Technology",
      founded: "2017",
      employees: "100+",
      services: ["Regulatory Compliance", "FinTech Solutions", "Cross-border Services", "EU Market Entry"],
      color: "#e3edfa"
    },
    {
      id: 5,
      name: "Vardaan Cyber",
      website: "https://www.vardaan-cyber.com/",
      description: "Founded in 2020, Vardaan Sdn Bhd is a forward-thinking cybersecurity firm based in Malaysia, specializing in innovative cybersecurity solutions, artificial intelligence, machine learning, and data science consulting. With a focus on safeguarding businesses from today's most complex cyber threats, we combine technical expertise with a client-centric approach, building solutions to address ever-changing cybersecurity challenges.",
      logo: vardaanCyberLogo,
      industry: "Cybersecurity",
      founded: "2019",
      employees: "80+",
      services: ["Threat Detection", "Incident Response", "Security Audits", "Compliance Management"],
      color: "#e3edfa"
    },
    {
      id: 6,
      name: "Vardaan Data Sciences",
      website: "https://vardaands.com/",
      description: "Through a combination of in-house expertise and a robust partner ecosystem, we are dedicated to empowering organizations to enhance efficiency, build resilience, and stay ahead in a rapidly changing landscape. Our portfolio includes AI-powered tools and intelligent platforms ranging from RiskaVaire intelligence platforms to smart logistics systems, audit management suites, camera vision AI, digital workers, and robotic automation solutions.",
      logo: vardaanAndsLogo,
      industry: "Business Consulting",
      founded: "2012",
      employees: "120+",
      services: ["Strategic Planning", "Business Consulting", "Organizational Development", "Growth Strategy"],
      color: "#e3edfa"
    }
  ];

  // Load logos from IndexedDB cache first, then update from S3 in background
  useEffect(() => {
    const loadLogosFromCache = async () => {
      try {
        console.log('📦 Group Companies: Loading logos from IndexedDB cache...');
        
        // Try to load from cache first
        const cachedData = await cacheService.getCache(LOGOS_CACHE_KEY, false);
        
        if (cachedData) {
          console.log('✅ Group Companies: Logos loaded from cache');
          setCachedLogos(cachedData);
        } else {
          // Initialize with local fallbacks
          const initialLogos = {};
          Object.keys(logoMapping).forEach(id => {
            initialLogos[id] = logoMapping[id].localFallback;
          });
          setCachedLogos(initialLogos);
        }
        
      } catch (error) {
        console.error('❌ Group Companies: Cache load error:', error);
        // Initialize with local fallbacks
        const initialLogos = {};
        Object.keys(logoMapping).forEach(id => {
          initialLogos[id] = logoMapping[id].localFallback;
        });
        setCachedLogos(initialLogos);
      }
    };
    
    const updateLogosCacheFromS3 = async () => {
      try {
        console.log('🌐 Group Companies: Updating logos cache from S3...');
        await assetService.loadS3Assets();
        
        // Load all logos from S3 with local fallbacks
        const updatedLogos = {};
        Object.keys(logoMapping).forEach(id => {
          const { s3Name, localFallback } = logoMapping[id];
          updatedLogos[id] = assetService.getAssetUrl(s3Name, localFallback);
        });
        
        // Update cache
        await cacheService.setCache(LOGOS_CACHE_KEY, updatedLogos);
        console.log('💾 Group Companies: Logos cache updated from S3');
        
        // Update UI
        setCachedLogos(updatedLogos);
        
      } catch (error) {
        console.error('❌ Group Companies: Failed to update logos cache from S3:', error);
      }
    };
    
    // Load from cache first (instant), then update from S3 in background
    loadLogosFromCache();
    updateLogosCacheFromS3();
  }, []); // Run once on mount

  const handleCompanyClick = (website) => {
    window.open(website, '_blank', 'noopener,noreferrer');
  };

  const handleImageLoad = (companyId) => {
    setLoadedImages(prev => ({
      ...prev,
      [companyId]: true
    }));
  };

  const handleImageError = (companyId) => {
    setLoadedImages(prev => ({
      ...prev,
      [companyId]: false
    }));
  };

  // Helper function to get logo URL from cache or fallback
  const getLogoUrl = (companyId) => {
    // If we have cached logos, use them
    if (cachedLogos[companyId]) {
      return cachedLogos[companyId];
    }
    // Otherwise fallback to local logo
    return logoMapping[companyId]?.localFallback || placeholderLogo;
  };

  return (
    <div className="group-companies-main-container">
      {/* Hero Section */}
      <section className="group-companies-hero">
        <div className="group-companies-hero-content">
          <h1 className="group-companies-hero-title">
            VARDAAN GLOBAL <span className="hero-highlight">GROUP</span>
          </h1>
          <p className="group-companies-hero-subtitle">
            A comprehensive ecosystem of specialized companies working together to deliver 
            cutting-edge solutions across technology, analytics, cybersecurity, and business consulting.
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="group-companies-overview">
        <div className="group-companies-container">
          <div className="overview-content">
            <p className="overview-description">
              A carefully curated portfolio of specialized companies delivering innovative solutions across technology, consulting, and digital transformation domains.
            </p>
          </div>
        </div>
      </section>

      {/* Companies Grid */}
      <section className="group-companies-grid-section">
        <div className="group-companies-container">
          <div className="companies-grid">
            {groupCompanies.map((company) => (
              <div key={company.id} className="company-card">
                {/* Logo Section */}
                <div className="company-logo-section">
                  <img 
                    src={getLogoUrl(company.id)} 
                    alt={`${company.name} logo`}
                    className="company-logo"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    onLoad={() => handleImageLoad(company.id)}
                    onError={() => handleImageError(company.id)}
                  />
                </div>
                
                {/* Company Info Section */}
                <div className="company-info-section">
                  <h3 className="company-name">{company.name}</h3>
                  <p className="company-description">{company.description}</p>
                  <button className="learn-more-btn" onClick={() => handleCompanyClick(company.website)}>
                    Explore
                    <FaExternalLinkAlt className="btn-icon" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="group-companies-cta">
        <div className="group-companies-container">
          <div className="cta-content">
            <h2 className="cta-title">Power your Organization with Our Products & Services</h2>
            <p className="cta-description">
              Whether you need specialized expertise from one of our companies or a comprehensive 
              solution spanning multiple disciplines, our group is ready to help you achieve your goals.
            </p>
            <div className="cta-buttons">
              <button 
                className="cta-secondary-btn"
                onClick={() => history.push('/contact#form')}
              >
                Get in Touch
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="footer-spacing"></div>
      <Footer />
    </div>
  );
};

export default GroupCompanies;
